// INVARIANT(scriptera): СЦЕНА-ПРИМЕР НАРИСОВАНА В ДВУХ МЕСТАХ — веб (apps/web: components/auth/app-onboarding.tsx,
// components/auth/onboarding.css, components/mock-phone.{tsx,css}, app/[locale]/wizard-scene/[scene]/*,
// lib/scene-script.ts) и iOS (scriptera-app: ios/App/App/Kit/Scenes/*). Правишь вид, шаг, текст-раскладку, тайминг,
// цвет или размер — правь ОБА места тем же днём и сверяй кадры склейкой «веб | iOS» (~/ops/native-scenes). Причина:
// владелец 26.10.2026 — сцены в приложении нативные, «пиксель в пиксель». Канон: ~/projects/NATIVE-SCENES-TZ.md,
// docs/HOW-IT-WORKS.md §«Сцены-примеры».

// ФРАЗЫ БОТА В НАРИСОВАННОМ ТЕЛЕФОНЕ (ТЗ-7 Ш3, владелец 2026-08-30: «при редактировании
// всё то же самое, только заполнить нужными фразами в телефоне всё»).
//
// На первом экране анкеты стоит ТОТ САМЫЙ телефон со сцены примеров работ (ТЗ-6). В
// правке он обязан показывать не выдуманную историю, а фразы ЭТОГО бота: кодовое слово,
// приветствие, кнопку, что отдаём. Сцену при этом НЕ ТРОГАЕМ ни строкой — здесь только
// строится её КОПИЯ с подменёнными текстами, и в неё же смотрит родной мастер (страница
// /[locale]/wizard-scene/[scene] принимает те же строки параметрами адреса).
//
// INVARIANT(scriptera): компонент сцены (`Scene`, `useScenes`, onboarding.css) и мокап
// телефона не правятся ради подмены — только копия определения снаружи. Причина: сцену
// делят лендинг, мини-тур и мастер, и она обязана выглядеть у всех одинаково (правило
// tour→landing sync). Канон: docs/NATIVE-WIZARD-PLAN.md, ТЗ-7 Ш3.
import type { SceneDef } from '@/lib/scene-script';

/** Какая сцена стоит у анкеты (то же, что `TemplateWizardDef.demoScene`). */
export type WizardScene = 's1' | 's2' | 's3' | 's4' | 's5' | 's6' | 's7' | 's8' | 'g1' | 'g2';
/** Сцены цели лендинга и тура (g1 «Подписки», g2 «Продажи», ТЗ GOAL-SCENES-HELP 23.10.2026): слова ниши
 *  подставляются как у s2 (раскладка ниже); гостю — ниша жребием, в кабинете — своя. */
export const GOAL_SCENES: readonly WizardScene[] = ['g1', 'g2'];
/** Сцены, которые может вставить статья справки (`::scene`): только восемь сцен мастеров и ролика — тип
 *  едет в приложение блоком статьи (замок контракта native-contract.snapshot), сцены цели туда не входят. */
export type HelpSceneKey = Exclude<WizardScene, 'g1' | 'g2'>;
export function isHelpScene(k: string): k is HelpSceneKey {
  return isWizardScene(k) && !GOAL_SCENES.includes(k);
}

/** Есть ли у сцены раскладка слотов. Ключ сцены приходит из дефа строкой. */
export function isWizardScene(k: string): k is WizardScene {
  return (
    k === 's1' || k === 's2' || k === 's3' || k === 's4' || k === 's5' || k === 's6' || k === 's7' || k === 's8' ||
    k === 'g1' || k === 'g2'
  );
}

/**
 * МЕСТА В СЦЕНЕ, которые анкета умеет заполнить своими словами. Имена — по смыслу
 * реплики, а не по номеру шага: пост, комментарий человека, публичный ответ бота,
 * переход в личные, приветствие, кнопка, что отдаём, имя файла.
 */
export const SCENE_SLOTS = [
  'post',
  'comment',
  'pub',
  'dm',
  'reply',
  'chip',
  'give',
  'file',
  'about',
  // Сообщения ЦЕПОЧКИ рассылок (s5, О5.8): четыре письма сцены по порядку. Столько же
  // мест, сколько bot-реплик у s5; сколько писем задал человек, столько и подменяем.
  'msg1',
  'msg2',
  'msg3',
  'msg4',
  // QUICK-START §10.4 (владелец 29.10: «сообщения, каждое из них с кнопкой»): подпись кнопки письма N — у настроенной
  // цепочки первая кнопка её письма, у статьи — кнопка, названная в тексте статьи. `SCENE_NO_BUTTON` — у письма кнопки
  // нет (правда этого бота/статьи): кнопка сцены после него снимается. Нет слота — кнопка сцены как есть.
  'btn1',
  'btn2',
  'btn3',
  'btn4',
] as const;

export type SceneSlot = (typeof SCENE_SLOTS)[number];

/** Чем анкета заполняет сцену; чего нет — остаётся собственный текст сцены. */
export type SceneTexts = Partial<Record<SceneSlot, string>>;

/** Предел длины одной строки: она едет параметром адреса, а не письмом. */
export const SCENE_TEXT_MAX = 300;

/**
 * МЕТКА «ФАЙЛА НЕТ» для слота `file` (Д7, владелец 03.09.2026): бот отдаёт подарок
 * ССЫЛКОЙ, значит карточки файла в разговоре не будет вовсе — шаг с ней из КОПИИ сцены
 * выбрасывается, а не подменяется именем подарка ниши («План тренировок.pdf», которого
 * бот не шлёт). Пустая строка для этого не годится: `clean()` пустые молча выкидывает,
 * и метка не доехала бы ни до копии сцены, ни параметром адреса в приложение.
 */
export const SCENE_NO_FILE = '!nofile';

/** Метка «у письма кнопки нет» для слотов `btn1`–`btn4` (как `SCENE_NO_FILE`: пустая строка в адрес не доедет). */
export const SCENE_NO_BUTTON = '!nobtn';

/** Письмо → слот его кнопки. */
const MSG_BUTTON: Partial<Record<SceneSlot, SceneSlot>> = { msg1: 'btn1', msg2: 'btn2', msg3: 'btn3', msg4: 'btn4' };

/** Приставка параметров адреса: /ru/wizard-scene/s2?t.comment=ФИГУРА */
const PREFIX = 't.';

type SceneStep = SceneDef['steps'][number];

/** Куда в сцене садится слот: номер шага и его вид (вид сверяем, чтобы правка сцены не
 *  привела к подмене не той реплики — не совпал, слот молча пропускается). */
interface Seat {
  slot: SceneSlot;
  at: number;
  kind: SceneStep['k'];
}

/** Раскладка слотов по сценам мастеров (порядок шагов — `useScenes`). */
const SEATS: Record<WizardScene, Seat[]> = {
  // Сцены цели (ТЗ GOAL-SCENES-HELP): слова ниши — как у s2 (пост, слово, публичный ответ, переход в
  // личные, ответ бота, выдача, файл); кнопки «Я подписался» / «Открыть в Телеграм» — свои, не слоты.
  // g1 «Подписки».
  g1: [
    { slot: 'post', at: 0, kind: 'post' },
    { slot: 'comment', at: 1, kind: 'draft' },
    { slot: 'pub', at: 2, kind: 'bot' },
    { slot: 'dm', at: 4, kind: 'jump' },
    { slot: 'reply', at: 5, kind: 'bot' },
    { slot: 'give', at: 9, kind: 'bot' },
    { slot: 'file', at: 10, kind: 'file' },
  ],
  // g2 «Продажи»: после перехода в Телеграм — отправленное «/start» (§10.12 п.3), выдача, файл, три сообщения серии.
  g2: [
    { slot: 'post', at: 0, kind: 'post' },
    { slot: 'comment', at: 1, kind: 'draft' },
    { slot: 'pub', at: 2, kind: 'bot' },
    { slot: 'dm', at: 4, kind: 'jump' },
    { slot: 'reply', at: 5, kind: 'bot' },
    // Кнопка ответа с подарком (у статьи — своя из её текста).
    { slot: 'chip', at: 6, kind: 'chip' },
    { slot: 'give', at: 9, kind: 'bot' },
    { slot: 'file', at: 10, kind: 'file' },
    { slot: 'msg1', at: 12, kind: 'bot' },
    { slot: 'msg2', at: 15, kind: 'bot' },
    { slot: 'msg3', at: 18, kind: 'bot' },
  ],
  // s1 «Подарок за подписку»: переписка в личных с нашим аккаунтом.
  s1: [
    { slot: 'comment', at: 0, kind: 'draft' },
    { slot: 'reply', at: 1, kind: 'bot' },
    { slot: 'chip', at: 2, kind: 'chip' },
    { slot: 'chip', at: 3, kind: 'echo' },
    { slot: 'give', at: 5, kind: 'bot' },
    { slot: 'file', at: 6, kind: 'file' },
  ],
  // s2 «Слово в комментарии»: пост, комментарий, публичный ответ, переход в личные.
  s2: [
    { slot: 'post', at: 0, kind: 'post' },
    { slot: 'comment', at: 1, kind: 'draft' },
    { slot: 'pub', at: 2, kind: 'bot' },
    { slot: 'dm', at: 5, kind: 'jump' },
    { slot: 'reply', at: 6, kind: 'bot' },
    { slot: 'chip', at: 7, kind: 'chip' },
    { slot: 'chip', at: 8, kind: 'echo' },
    { slot: 'give', at: 9, kind: 'bot' },
    { slot: 'file', at: 10, kind: 'file' },
  ],
  // s3 «Доводит до оплаты»: телеграм-бот с кнопками тарифов.
  s3: [
    { slot: 'reply', at: 2, kind: 'bot' },
    { slot: 'chip', at: 6, kind: 'chip' },
    { slot: 'give', at: 8, kind: 'bot' },
    { slot: 'file', at: 9, kind: 'file' },
  ],
  // s4 «Подарок за подписку» в Телеграме: заставка бота, «Начать», кнопка и выдача.
  // §10.12 п.3: заставки нет — первым шагом отправленное «/start».
  s4: [
    { slot: 'reply', at: 1, kind: 'bot' },
    { slot: 'chip', at: 2, kind: 'chip' },
    { slot: 'chip', at: 3, kind: 'echo' },
    { slot: 'give', at: 5, kind: 'bot' },
    { slot: 'file', at: 6, kind: 'file' },
  ],
  // s5 «Цепочка рассылок»: четыре письма-реплики бота (m0..m3 по дням). У НАСТРОЕННОЙ
  // цепочки (правка) показываем реальные письма человека по порядку — сколько задал,
  // столько мест и подменяем, остальные остаются текстом сцены (владелец 03.09.2026,
  // О5.8: настроенный бот в превью показывает своё). Паузы (дни) не трогаем — они у
  // сцены свои, а у цепочки любые; врать про сроки не нужно, письма важнее.
  // §10.12 п.3: первый шаг — отправленное «/start».
  // §11.20 п.1: письма подарка (m0) в сцене больше нет — мест три (m1..m3, последнее — продажа с «Купить»).
  s5: [
    { slot: 'msg1', at: 2, kind: 'bot' },
    { slot: 'msg2', at: 4, kind: 'bot' },
    { slot: 'msg3', at: 6, kind: 'bot' },
  ],
  // s6–s8 — сцены ролика (владелец 22.09.2026). В МАСТЕРЕ и на странице /wizard-scene
  // они стоковые (scene-view.tsx отдаёт деф как есть), а в статье справки «Как это
  // работает» (владелец 29.09.2026) под нишу и лицо клиента их подставляет
  // components/help/help-scene.tsx — этими раскладками.
  // s6 «Слово в комментарии — в Телеграм»: пост, слово, публичный ответ, директ с
  // просьбой подписаться (reply), после подписки — ссылка в Телеграм (give).
  s6: [
    { slot: 'post', at: 0, kind: 'post' },
    { slot: 'comment', at: 1, kind: 'draft' },
    { slot: 'pub', at: 2, kind: 'bot' },
    { slot: 'dm', at: 4, kind: 'jump' },
    { slot: 'reply', at: 5, kind: 'bot' },
    { slot: 'give', at: 8, kind: 'bot' },
  ],
  // s7 «Простой бот»: просьба человека (comment), приветствие, выдача и файл.
  s7: [
    { slot: 'comment', at: 0, kind: 'draft' },
    { slot: 'reply', at: 1, kind: 'bot' },
    { slot: 'give', at: 5, kind: 'bot' },
    { slot: 'file', at: 6, kind: 'file' },
  ],
  // s8 «Продвинутый бот»: приветствие в директе, выдача в Телеграме, файл, первое
  // сообщение цепочки (msg1); второе и третье у сцены нейтральные — их не трогаем.
  s8: [
    { slot: 'reply', at: 0, kind: 'bot' },
    { slot: 'give', at: 3, kind: 'bot' },
    { slot: 'file', at: 4, kind: 'file' },
    { slot: 'msg1', at: 6, kind: 'bot' },
  ],
};

/** Обрезанные и очищенные строки: пустые и пробельные не считаются ответом. */
function clean(texts: SceneTexts): SceneTexts {
  const out: SceneTexts = {};
  for (const slot of SCENE_SLOTS) {
    const raw = texts[slot];
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (value !== '') out[slot] = value.slice(0, SCENE_TEXT_MAX);
  }
  return out;
}

/** Строки анкеты → параметры адреса сцены (пустые не едут). */
export function sceneTextsToQuery(texts: SceneTexts): URLSearchParams {
  const out = new URLSearchParams();
  const src = clean(texts);
  for (const slot of SCENE_SLOTS) {
    const value = src[slot];
    if (value) out.set(`${PREFIX}${slot}`, value);
  }
  return out;
}

/** Параметры адреса сцены → строки анкеты (лишнее и длинное отсекается). */
export function sceneTextsFromQuery(
  query: Record<string, string | string[] | undefined>,
): SceneTexts {
  const out: SceneTexts = {};
  for (const slot of SCENE_SLOTS) {
    const raw = query[`${PREFIX}${slot}`];
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value === 'string') out[slot] = value;
  }
  return clean(out);
}

/** Новый шаг сцены с подменённым текстом (иммутабельно: старый не трогаем). */
function withText(step: SceneStep, text: string): SceneStep {
  if (step.k === 'file') return { ...step, name: text };
  if ('text' in step) return { ...step, text };
  return step;
}

/**
 * КОПИЯ СЦЕНЫ С ФРАЗАМИ ЭТОГО БОТА. Сама сцена не меняется — возвращается новое
 * определение (CLAUDE.md запрет №3, иммутабельность). Нечего подменять — возвращается
 * та же сцена.
 */
export function applySceneTexts(
  def: SceneDef,
  scene: WizardScene,
  texts: SceneTexts,
): SceneDef {
  const src = clean(texts);
  if (Object.keys(src).length === 0) return def;
  const seats = SEATS[scene];

  // СЛОВО В ПОСТЕ. Текста поста у анкеты нет (человек его не пишет — он висит в самом
  // Instagram), а кодовое слово в нём стоит: «Напиши ФИГУРА в комментарии — …». Поэтому
  // в посте меняем не всю строку, а само слово — на то, которое человек задал.
  const seatComment = seats.find((s) => s.slot === 'comment');
  const oldWord =
    seatComment && def.steps[seatComment.at]?.k === seatComment.kind
      ? ((def.steps[seatComment.at] as { text?: string }).text ?? '')
      : '';

  // ПОДАРОК ССЫЛКОЙ (Д7): карточки файла в разговоре нет — её шаг из копии выбрасываем.
  const noFile = src.file === SCENE_NO_FILE;

  const steps = def.steps.map((step, i) => {
    const seat = seats.find((s) => s.at === i && s.kind === step.k);
    if (seat) {
      const value = src[seat.slot];
      if (value && !(seat.slot === 'file' && noFile)) return withText(step, value);
      if (seat.slot === 'post' && src.comment && oldWord && 'text' in step) {
        return withText(step, step.text.split(oldWord).join(src.comment));
      }
    }
    return step;
  });

  const fileSeat = noFile ? seats.find((s) => s.slot === 'file') : undefined;
  const drop =
    fileSeat && def.steps[fileSeat.at]?.k === fileSeat.kind ? fileSeat.at : -1;
  return { ...def, steps: withLetterButtons(def.steps, steps, seats, src, drop) };
}

/**
 * КНОПКИ ПИСЕМ (§10.4): после письма N — кнопка из слота `btnN`. У письма уже стоит кнопка сцены — подпись меняется (и
 * ответ кнопкой следом); кнопки нет — вставляется; `SCENE_NO_BUTTON` — кнопка сцены после письма снимается вместе с
 * ответом. Номера шагов — исходные (`seats`), выброшенная карточка файла (`drop`) пропускается.
 */
function withLetterButtons(
  orig: readonly SceneStep[],
  mapped: readonly SceneStep[],
  seats: readonly Seat[],
  src: SceneTexts,
  drop: number,
): SceneStep[] {
  const steps = [...mapped];
  const skip = new Set<number>(drop >= 0 ? [drop] : []);
  const add = new Map<number, SceneStep>();
  for (const seat of seats) {
    const slot = MSG_BUTTON[seat.slot];
    const btn = slot ? src[slot] : undefined;
    if (!btn || orig[seat.at]?.k !== seat.kind || !src[seat.slot]) continue;
    const i = seat.at;
    if (steps[i + 1]?.k === 'chip') {
      const echo = steps[i + 2]?.k === 'echo';
      if (btn === SCENE_NO_BUTTON) {
        skip.add(i + 1);
        if (echo) skip.add(i + 2);
      } else {
        const prev = steps[i + 1] as Extract<SceneStep, { k: 'chip' }>;
        // §11.20 п.2: кнопка письма серии остаётся «не ждущей» (`auto`), если такой была у сцены.
        steps[i + 1] = { k: 'chip', text: btn, ...(prev.auto ? { auto: true as const } : {}) };
        if (echo) steps[i + 2] = { k: 'echo', text: btn };
      }
    } else if (btn !== SCENE_NO_BUTTON) {
      // Вставленная кнопка письма серии — без ответа кнопкой, сцена её не ждёт (§11.20 п.2).
      add.set(i, { k: 'chip', text: btn, auto: true });
    }
  }
  return steps.flatMap((st, i) => (skip.has(i) ? [] : add.has(i) ? [st, add.get(i)!] : [st]));
}
